if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      quantityRulesController = undefined;
      pendingRequestUrl = null;
      variantRequestPromise = undefined;
      resolvedStateSnapshot = undefined;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        /* Keep one known-good render to recover from a transport failure after
           an impossible option combination has hidden the buy-box regions.
           Dawn's normal success path replaces these regions again; this
           snapshot is used only by the scoped error path below. */
        this.captureResolvedState();
        /* Progressive enhancement contract for the JP variant picker. The
           server first exposes a native GET chooser that can reload the exact
           variant safely. Reveal the radio UI only after both Dawn custom
           elements and this pub/sub subscription are genuinely available.
           customElements.whenDefined waits for product-form.js, the next
           deferred asset, without exposing radios during a partial load. */
        Promise.all([
          customElements.whenDefined('variant-selects'),
          customElements.whenDefined('product-form'),
        ]).then(() => {
          if (!this.isConnected) return;
          const variantEngineReady = Boolean(this.variantSelectors && this.productForm);
          this.toggleAttribute('data-variant-engine-ready', variantEngineReady);
        });
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.abortController?.abort();
        this.quantityRulesController?.abort();
        this.pendingRequestUrl = null;
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        /* A cart-update refresh for the previous variant must not outlive a
           shopper's new selection. The selected-id guard in that refresh is
           the second line of defence against late responses. */
        this.quantityRulesController?.abort();
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        const requestController = new AbortController();
        this.abortController = requestController;

        this.variantRequestPromise = fetch(requestUrl, { signal: requestController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Variant update failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (this.abortController !== requestController || requestController.signal.aborted || !this.isConnected) return false;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            return true;
          })
          .then((applied) => {
            if (!applied) return;
            // set focus to last clicked option value
            const focusTarget = targetId ? document.getElementById(targetId) : null;
            if (focusTarget && this.contains(focusTarget)) focusTarget.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              /* A superseded request must never roll a newer selection back if
                 its transport rejects after abort raced with completion. */
              if (this.abortController !== requestController || requestController.signal.aborted || !this.isConnected) return;
              console.error(error);
              this.pendingRequestUrl = null;
              this.restoreResolvedState();
              const requestedFocusTarget = targetId ? document.getElementById(targetId) : null;
              const restoredFocusTarget = this.variantSelectors?.querySelector('input[type="radio"]:checked')
                || (requestedFocusTarget && this.contains(requestedFocusTarget) ? requestedFocusTarget : null);
              if (restoredFocusTarget) {
                try {
                  restoredFocusTarget.focus({ preventScroll: true });
                } catch (_) {
                  restoredFocusTarget.focus();
                }
              }
              /* Dawn has no terminal pub/sub event for a failed variant section
                 request. Give product-specific enhancements an explicit,
                 scoped failure signal so they can leave Loading safely. */
              this.dispatchEvent(new CustomEvent('product-info:variant-error', {
                bubbles: true,
                detail: { targetId, requestUrl },
              }));
            }
          })
          .finally(() => {
            if (this.abortController === requestController) this.abortController = undefined;
          });
        return this.variantRequestPromise;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          /* Copy the complete server-rendered purchase state. A disabled
             button can mean sold out, unavailable, or that the cart already
             reached the variant's effective maximum; replacing every label
             with "Sold out" would misstate the latter two states. */
          this.syncPurchaseControls(this.sectionId, html);

          this.captureResolvedState(html);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      get resolvedRegionIds() {
        return ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules'];
      }

      /* JP EDIT — Resolved commerce snapshots include successful cart refreshes.
         Upstream merge: keep native quantity/eligibility separate from transient
         Add ownership; rollback must restore the same cart capacity as the UI. */
      captureResolvedState(html) {
        const variantInputs = [...this.querySelectorAll(
          `#product-form-${this.dataset.section} input[name="id"], #product-form-installment-${this.dataset.section} input[name="id"]`
        )];
        const variantId = variantInputs.find((input) => input.value)?.value;
        if (!variantId) return;

        const submitButton = html?.getElementById(`ProductSubmitButton-${this.sectionId}`)
          || this.productForm?.querySelector('[type="submit"]');
        const dynamicCheckout = html?.querySelector('.jp-pp__dynamic') || this.querySelector('.jp-pp__dynamic');
        const stickyButton = html?.querySelector('[data-jp-sticky-atc]') || this.querySelector('[data-jp-sticky-atc]');
        const submitLabel = submitButton?.querySelector('span');
        this.resolvedStateSnapshot = {
          url: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          variantIds: variantInputs.map((input) => input.value),
          variantSelectorsHTML: this.variantSelectors?.innerHTML ?? null,
          regions: this.resolvedRegionIds.map((id) => {
            const element = this.querySelector(`#${id}-${this.dataset.section}`);
            return element ? { id, innerHTML: element.innerHTML, hidden: element.classList.contains('hidden') } : null;
          }).filter(Boolean),
          submit: submitButton ? {
            disabled: submitButton.disabled,
            ariaDisabled: submitButton.disabled ? 'true' : null,
            labelHTML: submitLabel?.innerHTML ?? null,
          } : null,
          quantity: this.quantityInput ? {
            attributes: ['data-cart-quantity', 'data-min', 'data-max', 'step', 'min', 'max'].map((name) =>
              [name, this.quantityInput.getAttribute(name)]
            ),
            value: this.quantityInput.value,
            regions: ['.quantity__rules', '.quantity__label'].map((selector) => {
              const element = this.quantityForm?.querySelector(selector);
              return element ? { selector, innerHTML: element.innerHTML } : null;
            }).filter(Boolean),
          } : null,
          dynamicHidden: dynamicCheckout?.hidden,
          sticky: stickyButton ? { disabled: stickyButton.disabled, text: stickyButton.textContent } : null,
        };
      }

      restoreResolvedState() {
        const snapshot = this.resolvedStateSnapshot;
        if (!snapshot) return;

        if (snapshot.variantSelectorsHTML !== null && this.variantSelectors) {
          HTMLUpdateUtility.setInnerHTML(this.variantSelectors, snapshot.variantSelectorsHTML);
        }
        snapshot.regions.forEach(({ id, innerHTML, hidden }) => {
          const element = this.querySelector(`#${id}-${this.dataset.section}`);
          if (!element) return;
          element.innerHTML = innerHTML;
          element.classList.toggle('hidden', hidden);
        });
        this.querySelectorAll(
          `#product-form-${this.dataset.section} input[name="id"], #product-form-installment-${this.dataset.section} input[name="id"]`
        ).forEach((input, index) => {
          input.value = snapshot.variantIds[index] ?? snapshot.variantIds[0] ?? '';
        });

        const submitButton = this.productForm?.querySelector('[type="submit"]');
        if (submitButton && snapshot.submit) {
          submitButton.disabled = snapshot.submit.disabled;
          if (this.productForm?.addRequest) submitButton.setAttribute('aria-disabled', 'true');
          else if (snapshot.submit.ariaDisabled === null) submitButton.removeAttribute('aria-disabled');
          else submitButton.setAttribute('aria-disabled', snapshot.submit.ariaDisabled);
          const submitLabel = submitButton.querySelector('span');
          if (submitLabel && snapshot.submit.labelHTML !== null) submitLabel.innerHTML = snapshot.submit.labelHTML;
        }

        if (this.quantityInput && snapshot.quantity) {
          snapshot.quantity.attributes.forEach(([name, value]) => {
            if (value === null) this.quantityInput.removeAttribute(name);
            else this.quantityInput.setAttribute(name, value);
          });
          this.quantityInput.value = snapshot.quantity.value;
          snapshot.quantity.regions.forEach(({ selector, innerHTML }) => {
            const element = this.quantityForm?.querySelector(selector);
            if (element) element.innerHTML = innerHTML;
          });
          publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
        const dynamicCheckout = this.querySelector('.jp-pp__dynamic');
        if (dynamicCheckout && snapshot.dynamicHidden !== undefined) dynamicCheckout.hidden = snapshot.dynamicHidden;
        const stickyButton = this.querySelector('[data-jp-sticky-atc]');
        if (stickyButton && snapshot.sticky) {
          stickyButton.disabled = snapshot.sticky.disabled;
          if (snapshot.sticky.disabled) stickyButton.setAttribute('aria-disabled', 'true');
          else stickyButton.removeAttribute('aria-disabled');
          stickyButton.textContent = snapshot.sticky.text;
        }

        if (this.dataset.updateUrl !== 'false' && snapshot.url) {
          window.history.replaceState({}, '', snapshot.url);
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity, 10)
            : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min, 10) : 1,
          max: this.quantityInput.dataset.max !== undefined
            ? parseInt(this.quantityInput.dataset.max, 10)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step, 10) : 1,
        };

        let min = data.min;
        const max = data.max === null || !Number.isFinite(data.max)
          ? null
          : Math.max(0, data.max - data.cartQuantity);
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max !== null) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        /* A cart event arriving during option resolution belongs to the next
           resolved (or rolled-back) variant. Do not apply A's cart response to
           B's pending controls or capture B's intent as A's resolved snapshot. */
        if (this.pendingRequestUrl && this.variantRequestPromise) {
          return this.variantRequestPromise.then(() => this.isConnected ? this.fetchQuantityRules() : undefined);
        }
        const requestedVariantId = this.productForm?.variantIdInput?.value;
        if (!requestedVariantId) return;

        this.quantityRulesController?.abort();
        const requestController = new AbortController();
        this.quantityRulesController = requestController;
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${this.dataset.section}`, {
          signal: requestController.signal,
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Quantity-rule refresh failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (
              this.quantityRulesController !== requestController
              || requestController.signal.aborted
              || this.productForm?.variantIdInput?.value !== requestedVariantId
            ) {
              return;
            }
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (!html.getElementById(`Quantity-Form-${this.dataset.section}`)
              || !html.getElementById(`ProductSubmitButton-${this.dataset.section}`)) {
              throw new Error('Quantity-rule refresh returned incomplete product markup');
            }
            this.updateQuantityRules(this.dataset.section, html);
            this.syncPurchaseControls(this.dataset.section, html);
            this.captureResolvedState(html);
          })
          .catch((e) => {
            if (e.name !== 'AbortError') console.error(e);
          })
          .finally(() => {
            if (this.quantityRulesController !== requestController) return;
            this.quantityRulesController = undefined;
            spinner?.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated || !this.quantityForm) return;
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }

        // Apply native min/max/value only after the selected variant's
        // quantity-rule and stock attributes have replaced the previous ones.
        this.setQuantityBoundries();
      }

      syncPurchaseControls(sectionId, html) {
        const currentButton = this.productForm?.submitButton;
        const updatedButton = html.getElementById(`ProductSubmitButton-${sectionId}`);
        const preserveChoiceGate = currentButton?.dataset.jpSizeGated === 'true';
        if (currentButton && updatedButton) {
          const shouldDisable = updatedButton.hasAttribute('disabled') || preserveChoiceGate;
          currentButton.toggleAttribute('disabled', shouldDisable);
          if (shouldDisable || this.productForm?.addRequest) currentButton.setAttribute('aria-disabled', 'true');
          else currentButton.removeAttribute('aria-disabled');
          if (!shouldDisable) this.productForm.error = false;

          const updatedText = updatedButton?.querySelector('span')?.textContent?.trim();
          if (!preserveChoiceGate && updatedText && this.productForm?.submitButtonText) {
            this.productForm.submitButtonText.textContent = updatedText;
          }
        }

        const currentDynamicCheckout = this.querySelector('.jp-pp__dynamic');
        const updatedDynamicCheckout = html.querySelector('.jp-pp__dynamic');
        if (currentDynamicCheckout && updatedDynamicCheckout) {
          currentDynamicCheckout.toggleAttribute(
            'hidden',
            updatedDynamicCheckout.hasAttribute('hidden') || currentDynamicCheckout.dataset.jpSizeGated === 'true'
          );
        }

        const currentStickyButton = this.querySelector('[data-jp-sticky-atc]');
        const updatedStickyButton = html.querySelector('[data-jp-sticky-atc]');
        if (currentStickyButton && updatedStickyButton) {
          const shouldDisable = updatedStickyButton.hasAttribute('disabled') || preserveChoiceGate;
          currentStickyButton.toggleAttribute('disabled', shouldDisable);
          if (shouldDisable) currentStickyButton.setAttribute('aria-disabled', 'true');
          else currentStickyButton.removeAttribute('aria-disabled');
          const updatedText = updatedStickyButton?.textContent?.trim();
          if (!preserveChoiceGate && updatedText) currentStickyButton.textContent = updatedText;
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
